import React from "react";
import { Box, Text, useColorModeValue } from "@chakra-ui/react";

// Constants to prevent inline object creation
const BOX_STYLES = {
  display: "flex" as const,
  flexDirection: "column" as const,
  paddingLeft: "0.75rem",
  borderLeft: "2px solid",
  borderLeftColor: "charli.primaryBlue",
  roundedRight: "md",
  backgroundColor: "transparent",
  transition: "background-color 200ms ease",
};

const POSITIVE_COLOR = "#3182ce";
const NEGATIVE_COLOR = "#f56565";
const HOVER_OPACITY = "0d";

// Utility functions extracted outside component
const parseNumericValue = (value: string | number | null | undefined): number => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    // Simplified regex patterns for better performance
    const hasNegative = /^[^0-9]*-/.test(value);
    const digits = value.match(/[\d.]+/g)?.join("") ?? "0";
    const numberValue = parseFloat(digits);
    return hasNegative ? -numberValue : numberValue;
  }
  return 0;
};

const isKeyMetricNegative = (value: string | number | null | undefined, isNegativeGood?: boolean): boolean => {
  const numberValue = parseNumericValue(value);
  return numberValue <= 0 || (numberValue > 0 && isNegativeGood === true);
};

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  value: string | number | null | undefined;
  isNegativeGood?: boolean;
}

function FinancialMetric({ title, value, isNegativeGood, ...props }: Props) {
  const titleColor = useColorModeValue("primary.darkGray", "gray.400");

  const valueColor = React.useMemo(
    () => (isKeyMetricNegative(value, isNegativeGood) ? NEGATIVE_COLOR : POSITIVE_COLOR),
    [value, isNegativeGood]
  );

  const hoverStyle = React.useMemo(
    () => ({
      backgroundColor: valueColor + HOVER_OPACITY,
    }),
    [valueColor]
  );

  return (
    <Box {...BOX_STYLES} _hover={hoverStyle} {...props}>
      <Text fontSize="2xl" color={valueColor}>
        {value}
      </Text>
      <Text fontSize="xs" color={titleColor}>
        {title}
      </Text>
    </Box>
  );
}

export default React.memo(FinancialMetric);
